import * as THREE from 'three';
import { CARD_WIDTH, CARD_HEIGHT, CARD_THICKNESS } from '../cardMesh';

// ── Dragonlord ready aura ────────────────────────────────────────
//
// Subtle amber glow under the Dragonlord avatar while its invoke
// ability ("pay (3) to gain the set-aside Dragon's abilities") is
// actually payable: the controller's own turn, threshold met, mana
// available, not yet used. Driven per-frame by
// avatarAbilities.syncDragonlordReadyGlow. This module only owns
// the mesh.
//
// Same contract as the CARD_STATUS_EFFECTS trios (create / update /
// dispose, `.points` is the scene-root Object3D) so the render loop
// treats it like any other card-anchored effect.

const RISE_HEIGHT_OFFSET = 0.05;

const GLOW_VERT = /* glsl */ `
  varying vec2 vLocalXY;
  void main() {
    vLocalXY = position.xy;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const GLOW_FRAG = /* glsl */ `
  precision highp float;

  uniform float uTime;
  uniform float uStrength;
  uniform vec2  uCardSize;
  uniform vec3  uColor;

  varying vec2 vLocalXY;

  void main() {
    vec2 p = vLocalXY;

    // Signed distance to the card rectangle. Negative inside.
    vec2 q = abs(p) - uCardSize * 0.5;
    float sdf = length(max(q, vec2(0.0))) + min(max(q.x, q.y), 0.0);

    // Halo band hugging the card edge: brightest right at the rim,
    // fading both outward (the aura) and inward (so the art stays
    // readable). Band widths scale with the card so the aura reads
    // the same regardless of world-unit card size.
    float outer = 1.0 - smoothstep(0.0, uCardSize.x * 0.16, sdf);
    float inner = smoothstep(-uCardSize.x * 0.08, 0.0, sdf);
    float rim = outer * inner;

    // Slow breathing pulse so the ready state reads as alive without
    // shouting over the rest of the board.
    float pulse = 0.7 + 0.3 * sin(uTime * 2.2);
    float intensity = rim * pulse * 0.5 * uStrength;

    if (intensity < 0.004) discard;
    gl_FragColor = vec4(uColor * intensity, intensity);
  }
`;

export function createDragonlordReadyGlow() {
  const geometry = new THREE.PlaneGeometry(CARD_WIDTH * 1.6, CARD_HEIGHT * 1.5);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: Math.random() * 100 },
      // Ramps 0 → 1 on spawn so the aura fades in instead of popping.
      uStrength: { value: 0 },
      uCardSize: { value: new THREE.Vector2(CARD_WIDTH, CARD_HEIGHT) },
      uColor: { value: new THREE.Color(0xd99a3d) },
    },
    vertexShader: GLOW_VERT,
    fragmentShader: GLOW_FRAG,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;
  mesh.position.z = CARD_THICKNESS / 2 + RISE_HEIGHT_OFFSET;
  mesh.renderOrder = 16;

  const group = new THREE.Group();
  group.frustumCulled = false;
  group.add(mesh);
  return { points: group, material, geometry };
}

const _tmpVec3 = new THREE.Vector3();
const _tmpEuler = new THREE.Euler();
export function updateDragonlordReadyGlow(effect, dt, cardMesh) {
  if (!effect || !cardMesh) return;
  // Stay tied to the card: when region-view hides the avatar the aura
  // hides with it instead of bleeding into the other region view.
  effect.points.visible = cardMesh.visible;
  effect.material.uniforms.uTime.value += dt;
  const strength = effect.material.uniforms.uStrength;
  strength.value = Math.min(1, strength.value + dt * 2.5);

  cardMesh.getWorldPosition(_tmpVec3);
  effect.points.position.copy(_tmpVec3);

  const intent = cardMesh.userData?.intentEuler;
  const yaw = intent ? intent.z : 0;
  _tmpEuler.set(-Math.PI / 2, 0, yaw, 'XYZ');
  effect.points.rotation.copy(_tmpEuler);
}

export function disposeDragonlordReadyGlow(effect) {
  if (!effect) return;
  effect.points.parent?.remove(effect.points);
  effect.geometry?.dispose();
  effect.material?.dispose();
}
